import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Plus, Trash2, Save, X, Loader2, Megaphone, Upload, Image, Video, 
  Pin, AlertTriangle, Calendar, Edit, Check, Clock, Play
} from "lucide-react";
import { getImageUrl } from "@/lib/imageUtils";

const AnnouncementsManager = () => {
  const { authAxios } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const editImageRef = useRef(null);
  const editVideoRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    post_type: "update",
    image_url: "",
    video_url: "",
    is_pinned: false,
    is_urgent: false,
    author: "Coach"
  });

  const postTypes = [
    { value: "update", label: "Update", icon: Megaphone, color: "text-neon-blue" },
    { value: "alert", label: "Alert", icon: AlertTriangle, color: "text-red-500" },
    { value: "event", label: "Event", icon: Calendar, color: "text-neon-green" },
    { value: "video", label: "Video Post", icon: Video, color: "text-neon-pink" }
  ];

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/announcements`);
      setAnnouncements(response.data);
    } catch (e) {
      toast.error("Failed to fetch announcements");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, isEdit = false) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('category', 'announcements');

      const response = await authAxios.post('/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const fullUrl = `${API.replace('/api', '')}${response.data.url}`;
      
      if (isEdit && editingPost) {
        setEditingPost({ ...editingPost, image_url: fullUrl });
      } else {
        setFormData({ ...formData, image_url: fullUrl });
      }
      toast.success("Image uploaded!");
    } catch (e) {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleVideoUpload = async (e, isEdit = false) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 100 * 1024 * 1024) {
      toast.error("Video too large. Max 100MB");
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('category', 'announcement_videos');

      const response = await authAxios.post('/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      const fullUrl = `${API.replace('/api', '')}${response.data.url}`;
      
      if (isEdit && editingPost) {
        setEditingPost({ ...editingPost, video_url: fullUrl });
      } else {
        setFormData({ ...formData, video_url: fullUrl });
      }
      toast.success("Video uploaded!");
    } catch (e) {
      toast.error("Failed to upload video");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await authAxios.post('/announcements', formData);
      toast.success("Post published!");
      setShowForm(false);
      resetForm();
      fetchAnnouncements();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create post");
    }
  };

  const handleEdit = (post) => {
    setEditingPost({ ...post });
    setShowForm(false);
  };

  const handleSaveEdit = async () => {
    if (!editingPost) return;
    
    try {
      const payload = {
        title: editingPost.title,
        content: editingPost.content,
        post_type: editingPost.post_type,
        image_url: editingPost.image_url || null,
        video_url: editingPost.video_url || null,
        is_pinned: editingPost.is_pinned,
        is_urgent: editingPost.is_urgent,
        author: editingPost.author
      };
      
      await authAxios.put(`/announcements/${editingPost.id}`, payload);
      toast.success("Post updated!");
      setEditingPost(null);
      fetchAnnouncements();
    } catch (e) {
      toast.error("Failed to update post");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    
    try {
      await authAxios.delete(`/announcements/${id}`);
      toast.success("Post deleted");
      if (editingPost?.id === id) setEditingPost(null);
      fetchAnnouncements();
    } catch (e) {
      toast.error("Failed to delete post");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      content: "",
      post_type: "update",
      image_url: "",
      video_url: "",
      is_pinned: false,
      is_urgent: false,
      author: "Coach"
    });
  };

  const getTypeConfig = (type) => {
    return postTypes.find(t => t.value === type) || postTypes[0];
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' 
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-neon-blue animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-orbitron text-xl text-white flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-neon-blue" />
          Message Board ({announcements.length})
        </h2>
        <button
          onClick={() => { setShowForm(!showForm); setEditingPost(null); }}
          data-testid="add-post-btn"
          className="cyber-btn py-2 px-4 text-xs"
        >
          <span className="flex items-center gap-2">
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? "CANCEL" : "NEW POST"}
          </span>
        </button>
      </div>

      {/* Add Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="cyber-card p-6 space-y-4 border-neon-blue/30" data-testid="post-form">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-neon-blue/30">
            <Plus className="w-5 h-5 text-neon-blue" />
            <span className="font-orbitron text-sm text-neon-blue">CREATE NEW POST</span>
          </div>

          {/* Title and Type */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2 space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
                placeholder="Post title..."
                className="bg-void-black border-white/10"
                data-testid="post-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Type *</Label>
              <Select value={formData.post_type} onValueChange={(v) => setFormData({...formData, post_type: v})}>
                <SelectTrigger className="bg-void-black border-white/10" data-testid="post-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-void-black border-white/10">
                  {postTypes.map(type => {
                    const Icon = type.icon;
                    return (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${type.color}`} />
                          <span>{type.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-2">
            <Label className="font-orbitron text-xs text-gray-400">Message Content *</Label>
            <Textarea
              value={formData.content}
              onChange={(e) => setFormData({...formData, content: e.target.value})}
              required
              placeholder="Write your message here..."
              rows={5}
              className="bg-void-black border-white/10 font-rajdhani"
              data-testid="post-content-input"
            />
          </div>

          {/* Media Upload Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Image Upload */}
            <div className="border border-dashed border-neon-blue/50 p-4">
              <Label className="font-orbitron text-xs text-neon-blue mb-3 flex items-center gap-2">
                <Image className="w-4 h-4" />
                IMAGE (Optional)
              </Label>
              
              <div className="flex items-center gap-3">
                <div className="w-20 h-20 bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden">
                  {formData.image_url ? (
                    <img src={getImageUrl(formData.image_url)} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <Image className="w-6 h-6 text-gray-600" />
                  )}
                </div>
                <div className="flex-1">
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, false)}
                    className="hidden"
                    id="post-image-upload"
                  />
                  <label
                    htmlFor="post-image-upload"
                    data-testid="post-image-upload-btn"
                    className={`cyber-btn py-2 px-3 text-xs cursor-pointer inline-flex items-center gap-2 ${uploading ? 'opacity-50' : ''}`}
                  >
                    <Upload className="w-3 h-3" />
                    UPLOAD
                  </label>
                  {formData.image_url && (
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, image_url: ''})}
                      className="ml-2 text-red-500 text-xs"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Video Upload */}
            <div className="border border-dashed border-neon-pink/50 p-4">
              <Label className="font-orbitron text-xs text-neon-pink mb-3 flex items-center gap-2">
                <Video className="w-4 h-4" />
                VIDEO (Optional)
              </Label>
              
              <div className="flex items-center gap-3">
                <div className="w-20 h-20 bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden">
                  {formData.video_url ? (
                    <Play className="w-8 h-8 text-neon-pink" />
                  ) : (
                    <Video className="w-6 h-6 text-gray-600" />
                  )}
                </div>
                <div className="flex-1">
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/*"
                    onChange={(e) => handleVideoUpload(e, false)}
                    className="hidden"
                    id="post-video-upload"
                  />
                  <label
                    htmlFor="post-video-upload"
                    data-testid="post-video-upload-btn"
                    className={`cyber-btn cyber-btn-pink py-2 px-3 text-xs cursor-pointer inline-flex items-center gap-2 ${uploading ? 'opacity-50' : ''}`}
                  >
                    <Upload className="w-3 h-3" />
                    UPLOAD
                  </label>
                  {uploading && uploadProgress > 0 && (
                    <div className="mt-2 w-full h-1 bg-white/10">
                      <div className="h-full bg-neon-pink" style={{ width: `${uploadProgress}%` }} />
                    </div>
                  )}
                  {formData.video_url && (
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, video_url: ''})}
                      className="ml-2 text-red-500 text-xs"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Options */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-white/5 border border-white/10">
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Author</Label>
              <Input
                value={formData.author}
                onChange={(e) => setFormData({...formData, author: e.target.value})}
                placeholder="Coach"
                className="bg-void-black border-white/10 text-sm"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={formData.is_pinned}
                onCheckedChange={(v) => setFormData({...formData, is_pinned: v})}
              />
              <div>
                <Label className="font-orbitron text-xs text-yellow-500 flex items-center gap-1">
                  <Pin className="w-3 h-3" />
                  PIN POST
                </Label>
                <p className="font-rajdhani text-[10px] text-gray-500">Keep at top</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={formData.is_urgent}
                onCheckedChange={(v) => setFormData({...formData, is_urgent: v})}
              />
              <div>
                <Label className="font-orbitron text-xs text-red-500 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  URGENT
                </Label>
                <p className="font-rajdhani text-[10px] text-gray-500">Highlight alert</p>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end">
            <button type="submit" className="cyber-btn py-2 px-6 text-xs" data-testid="publish-post-btn">
              <span className="flex items-center gap-2">
                <Megaphone className="w-4 h-4" />
                PUBLISH POST
              </span>
            </button>
          </div>
        </form>
      )}

      {/* Posts List */}
      <div className="space-y-4">
        {announcements.map(post => {
          const config = getTypeConfig(post.post_type);
          const TypeIcon = config.icon;
          const isEditing = editingPost?.id === post.id;

          return (
            <div
              key={post.id}
              className={`cyber-card overflow-hidden ${isEditing ? 'ring-2 ring-neon-green' : ''} ${post.is_urgent ? 'border-red-500/50' : ''}`}
              data-testid={`post-item-${post.id}`}
            >
              {isEditing ? (
                /* Edit Mode */
                <div className="p-6 space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b border-neon-green/30">
                    <Edit className="w-4 h-4 text-neon-green" />
                    <span className="font-orbitron text-xs text-neon-green">EDITING POST</span>
                  </div>

                  <Input
                    value={editingPost.title}
                    onChange={(e) => setEditingPost({...editingPost, title: e.target.value})}
                    className="bg-void-black border-white/10"
                    placeholder="Title"
                  />
                  
                  <Textarea
                    value={editingPost.content}
                    onChange={(e) => setEditingPost({...editingPost, content: e.target.value})}
                    rows={4}
                    className="bg-void-black border-white/10 font-rajdhani"
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <input
                        ref={editImageRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, true)}
                        className="hidden"
                        id={`edit-image-${post.id}`}
                      />
                      <label htmlFor={`edit-image-${post.id}`} className="cyber-btn py-1 px-3 text-xs cursor-pointer">
                        <Image className="w-3 h-3 mr-1 inline" />
                        {editingPost.image_url ? 'Change Image' : 'Add Image'}
                      </label>
                      {editingPost.image_url && (
                        <button onClick={() => setEditingPost({...editingPost, image_url: ''})} className="text-red-500 text-xs">Remove</button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        ref={editVideoRef}
                        type="file"
                        accept="video/*"
                        onChange={(e) => handleVideoUpload(e, true)}
                        className="hidden"
                        id={`edit-video-${post.id}`}
                      />
                      <label htmlFor={`edit-video-${post.id}`} className="cyber-btn cyber-btn-pink py-1 px-3 text-xs cursor-pointer">
                        <Video className="w-3 h-3 mr-1 inline" />
                        {editingPost.video_url ? 'Change Video' : 'Add Video'}
                      </label>
                      {editingPost.video_url && (
                        <button onClick={() => setEditingPost({...editingPost, video_url: ''})} className="text-red-500 text-xs">Remove</button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Switch checked={editingPost.is_pinned} onCheckedChange={(v) => setEditingPost({...editingPost, is_pinned: v})} />
                      <span className="text-xs text-yellow-500">Pinned</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Switch checked={editingPost.is_urgent} onCheckedChange={(v) => setEditingPost({...editingPost, is_urgent: v})} />
                      <span className="text-xs text-red-500">Urgent</span>
                    </label>
                  </div>

                  <div className="flex justify-between pt-2 border-t border-white/10">
                    <button onClick={() => setEditingPost(null)} className="text-gray-500 hover:text-white text-xs flex items-center gap-1">
                      <X className="w-4 h-4" /> Cancel
                    </button>
                    <button onClick={handleSaveEdit} className="cyber-btn py-2 px-4 text-xs border-neon-green text-neon-green hover:bg-neon-green hover:text-black">
                      <Check className="w-4 h-4 mr-1 inline" /> SAVE CHANGES
                    </button>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <>
                  {post.is_urgent && (
                    <div className="bg-red-500 px-3 py-1 text-center">
                      <span className="font-orbitron text-[10px] text-white">⚠ URGENT</span>
                    </div>
                  )}
                  
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`p-2 ${getTypeConfig(post.post_type).color.replace('text-', 'bg-')}/10`}>
                          <TypeIcon className={`w-4 h-4 ${config.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {post.is_pinned && <Pin className="w-3 h-3 text-yellow-500" />}
                            <h3 className="font-orbitron text-sm text-white truncate">{post.title}</h3>
                          </div>
                          <p className="font-rajdhani text-xs text-gray-500 mt-1 line-clamp-2">{post.content}</p>
                          <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-600">
                            <span>{post.author}</span>
                            <span>•</span>
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(post.created_at)}</span>
                            {post.video_url && <span className="text-neon-pink flex items-center gap-1"><Play className="w-3 h-3" />Video</span>}
                            {post.image_url && !post.video_url && <span className="text-neon-blue flex items-center gap-1"><Image className="w-3 h-3" />Image</span>}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleEdit(post)} className="p-2 text-gray-500 hover:text-neon-green hover:bg-neon-green/10 rounded" data-testid={`edit-post-${post.id}`}>
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(post.id)} className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-500/10 rounded" data-testid={`delete-post-${post.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {announcements.length === 0 && (
        <div className="text-center py-12 cyber-card" data-testid="empty-posts-state">
          <Megaphone className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="font-rajdhani text-gray-500">No posts yet. Create your first announcement!</p>
        </div>
      )}
    </div>
  );
};

export default AnnouncementsManager;
